import { useEffect } from 'react';
import type { EndUser, Integration } from '@amplitude/engagement-browser';

/**
 * The event the SDK hands to an integration, taken from the package's own
 * `Integration` rather than from `@amplitude/analytics-types`. That package is
 * a transitive dependency of the SDK, not one of ours, so importing it directly
 * would reach through the dependency tree for a type the SDK already exposes.
 */
type EngagementEvent = Parameters<NonNullable<Integration['track']>>[0];

/**
 * Which app of the HG platform these guides belong to.
 *
 * The same value the Segment events already carry, so a guide scoped to Studio
 * and a chart counting Studio describe the same product. The Amplitude project
 * is shared across the platform, so an announcement without this is one every
 * app shows.
 */
const APP = 'studio';

/**
 * Which Amplitude data centre the project lives in. A constant rather than a
 * build global because it is a property of the Amplitude project, not of a
 * deployment, and a build pointed at the wrong zone would silently find no
 * guides at all rather than fail.
 */
const SERVER_ZONE = 'US';

/**
 * How often the SDK re-reads what it is allowed to show.
 *
 * Left unset, auto-refresh is off and the SDK reads the user's interaction
 * state once, at boot. That state holds "already dismissed", "already
 * completed" and the throttle counters, and this is an app people leave open
 * all day: without a refresh, an announcement dismissed in another tab shows
 * again here, and a throttle spent elsewhere is not seen. Hourly is Amplitude's
 * own example and well below the rate any of it changes; the SDK's floor is 60
 * seconds. Throttling itself stays configured in Amplitude.
 */
const AUTO_REFRESH_INTERVAL_SECONDS = 3600;

/**
 * Whether the loader has been installed on `window.engagement` for this
 * document. `init` is idempotent by contract, but it also injects the CDN
 * script tag, so a second call is a second request for nothing.
 */
let initialized = false;

/**
 * Whether a boot has been issued. Set before the boot resolves, so an identity
 * change still shuts down a boot that is in flight: the SDK holds the booted
 * user until it is told otherwise.
 */
let booted = false;

/**
 * Which start is current. A start resolves a dynamic import before it can touch
 * the SDK, and by then the identity it was started for may already be gone.
 */
let generation = 0;

/**
 * Everything a guide's conditions can be written against.
 *
 * Amplitude's Conditional Logic reads user properties and survey answers, and
 * nothing else. Studio ships no surveys, so this is the whole of what a product
 * announcement can branch on.
 *
 * The properties have to arrive through the SDK, during the session they apply
 * to. The same facts already reach Amplitude as Segment traits, but a condition
 * cannot read those: a property that only ever travelled through the identify
 * reads as absent and the condition never matches.
 */
export interface GuidesIdentity {
  /** The MadKudu user ObjectId, the same id the Segment identify is keyed by. */
  userId: string;
  /** The tenant being worked in, for announcing to one customer at a time. */
  tenant?: number;
  /** Whether the viewer is one of us, for announcing internally first. */
  isInternalUser: boolean;
}

/**
 * Builds the user a guide is targeted at and conditioned on.
 *
 * Keyed by the MadKudu user ObjectId rather than by email, for the reason the
 * Segment identify is, and here it matters twice over: Amplitude keys what a
 * user has already seen, dismissed and rage closed to this id, so an unstable
 * one makes every visit a new user who has seen nothing.
 *
 * @param identity - The session this boot is for.
 * @returns The user to boot with.
 */
function toEndUser(identity: GuidesIdentity): EndUser {
  return {
    user_id: identity.userId,
    user_properties: {
      app: APP,
      tenant: identity.tenant,
      isInternalUser: identity.isInternalUser,
    },
  };
}

/**
 * Builds the callback that sends the SDK's own events to Segment.
 *
 * The names belong to Amplitude's SDK, downloaded from their CDN at runtime, so
 * they are carried through as they arrive. Without this the SDK collects its
 * guide impressions in the browser and reports none of them.
 *
 * `tenant` rides along because Studio's own events carry it and are filtered by
 * it; it is read from the identity this boot was made with, which a tenant
 * change replaces by booting again.
 *
 * @param identity - The session whose events these are.
 * @returns The integration callback to boot with.
 */
function forwardToSegment(identity: GuidesIdentity): Integration {
  return {
    track: (event: EngagementEvent) => {
      window.analytics.track(event.event_type, {
        app: APP,
        tenant: identity.tenant,
        ...event.event_properties,
      });
    },
  };
}

/**
 * Loads the SDK and makes guides available to one user.
 *
 * No key means the feature is off, which is the state of every environment not
 * provisioned yet and of the server render. The import is dynamic so an
 * unprovisioned build never downloads the loader, and so the CDN script tag is
 * injected once a user is known rather than while the app is still starting.
 *
 * Nothing here can reject into the caller. A blocked CDN, an ad blocker or a
 * failed boot leaves the app without guides, which is not a reason to break the
 * page that was meant to show them.
 *
 * @param identity - The user to boot for, and the facts a guide can branch on.
 */
export async function startGuides(identity: GuidesIdentity): Promise<void> {
  if (!AMPLITUDE_API_KEY || typeof window === 'undefined') {
    return;
  }

  const started = (generation += 1);

  try {
    const { init } = await import('@amplitude/engagement-browser');
    if (started !== generation) {
      return;
    }

    if (!initialized) {
      init(AMPLITUDE_API_KEY, {
        serverZone: SERVER_ZONE,
        autoRefreshIntervalSeconds: AUTO_REFRESH_INTERVAL_SECONDS,
      });
      initialized = true;
    }

    booted = true;
    await window.engagement.boot({
      user: toEndUser(identity),
      integrations: [forwardToSegment(identity)],
    });
  } catch {
    return;
  }
}

/**
 * Takes the guides away again, and cancels any start still resolving.
 *
 * Called when the identity changes rather than only at unmount, because the SDK
 * holds the booted user until it is shut down: booting a second user on top of
 * the first is what the SDK's own docs tell you not to do.
 */
export function stopGuides(): void {
  generation += 1;
  if (!booted) {
    return;
  }
  booted = false;

  try {
    window.engagement.shutdown();
  } catch {
    return;
  }
}

/**
 * Runs Amplitude guides for the signed-in user.
 *
 * Mounted from the page that already knows the session, and gated on the user
 * id for the same reason its page views are gated on the email: a guide booted
 * before the identity is known is one targeted at nobody. The effect is also
 * what keeps this out of the server render.
 *
 * No god-mode guard here, unlike Market Analyzer. A superkudu in Studio changes
 * the tenant they are working in, and the booted id stays their own, so the
 * announcements they open and dismiss are spent against their own record.
 * Market Analyzer's impersonation instead swaps the access token for the
 * target's, which is what makes a guard necessary there.
 *
 * @param identity - The session, or undefined before it is known.
 */
export function useGuides(identity: Partial<GuidesIdentity> | undefined): void {
  const { userId, tenant, isInternalUser } = identity ?? {};

  useEffect(() => {
    if (!userId) {
      return;
    }

    void startGuides({
      userId,
      tenant,
      isInternalUser: Boolean(isInternalUser),
    });

    return stopGuides;
  }, [userId, tenant, isInternalUser]);
}
